import { html } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
import { ref } from 'lit/directives/ref.js';

import { requiredValidator } from '../utilities/validators';
import { FloatField } from './float_field';
import { formatString } from '../utilities/utils';
import { watchAll } from '../utilities/watch';
import { twoWayBind } from '../utilities/twoWayBind';

export class FloatDateField extends FloatField {
  static get properties() {
    return {
      ...super.properties,
      formattedDate: {
        type: String,
        reflect: true,
        attribute: 'formatted-date',
      },
      inputFormat: { type: String, attribute: 'input-format' },
      date: { type: String, attribute: false },
      value: { type: String, attribute: true },
      min: {
        type: Date,
        attribute: 'min',
        converter: {
          fromAttribute: (value) => (value ? new Date(value) : null),
        },
      },
      max: {
        type: Date,
        attribute: 'max',
        converter: {
          fromAttribute: (value) => (value ? new Date(value) : null),
        },
      },
    };
  }

  constructor() {
    super();
    this._oldValue = null;
    this._date = null;
    this._inputDateParts = [];
    this._inputFormat = '';
    this.formattedDate = '';
    this.type = "date"

    this.validators = [
      {
        ...requiredValidator,
        message: () => 'Please enter a valid date',
        isValid: () => (this.required ? !!this._value : true),
      },
      {
        key: 'rangeUnderflow',
        message: () =>
          formatString(
            'You have to be born after {0}',
            this.formatDateForDisplay(this.min)
          ),
        isValid: () => !this.min || this._value >= this.min,
      },
      {
        key: 'rangeOverflow',
        message: () =>
          formatString(
            "You couldn't have been born after {0}",
            this.formatDateForDisplay(this.max)
          ),
        isValid: () => !this.max || this._value <= this.max,
      },
    ];

    twoWayBind(this, 'date', 'change', 'detail');
    twoWayBind(this, 'value', 'change', 'detail');

    watchAll(this, [
      {
        propName: 'locale',
        handlerName: 'setDefaultMask',
        options: { waitUntilFirstUpdate: true },
      },
      {
        propName: 'displayFormat',
        handlerName: 'setDisplayFormat',
        options: { waitUntilFirstUpdate: true },
      },
      {
        propName: 'prompt',
        handlerName: 'promptChange',
        options: { waitUntilFirstUpdate: true },
      },
      {
        propName: 'min',
        handlerName: 'constraintChange',
        options: { waitUntilFirstUpdate: true },
      },
      {
        propName: 'max',
        handlerName: 'constraintChange',
        options: { waitUntilFirstUpdate: true },
      },
    ]);
  }

  get inputFormat() {
    return this._inputFormat;
  }

  set inputFormat(val) {
    if (val) {
      this._inputFormat = val;
    }
  }

  get date() {
    return this._date;
  }

  set date(val) {
    if (!val) {
      this._date = null;
      this.formattedDate = '';
      this.updateFormValue();
      this.updateValidity();
      this.setInvalidState();
      return;
    }

    this._date = val;
    this.formattedDate = this.dateFormater(val);
    if (!this.hasInteracted) return;

    this.updateFormValue();
    this.updateValidity();
    this.setInvalidState();
  }

  formatDateForDisplay(date) {
    if (!date || !(date instanceof Date) || isNaN(date)) return '';
    return `${date.getDate().toString().padStart(2, '0')} ${date.toLocaleString(
      'default',
      { month: 'long' }
    )} ${date.getFullYear()}`;
  }

  get value() {
    return this._value;
  }

  set value(val) {
    if (typeof val === 'string') {
      const parsedDate = new Date(val);
      this._value = !isNaN(parsedDate) ? parsedDate : null;
    } else {
      this._value = val;
    }
    this.date = this._value;
    if (!this.hasInteracted) return;

    this.updateFormValue();
    this.updateValidity();
    this.setInvalidState();
  }

  connectedCallback() {
    super.connectedCallback();
    this.updateValidity();
  }

  dateFormater(val) {
    return `${val.getFullYear()}-${`0${val.getMonth() + 1}`.slice(
      -2
    )}-${`0${val.getDate()}`.slice(-2)}`;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);

    if (changedProperties.has('min') || changedProperties.has('max')) {
      this.updateValidity();
    }
  }

  updateFormValue() {
    this._value ? this.setFormValue(this._value) : this.setFormValue(null);
  }

  get minValue() {
    return this.min;
  }

  get maxValue() {
    return this.max;
  }

  constraintChange() {
    this.updateValidity();
  }

  /** Clears the input element of user input. */
  clear() {
    this._date = null;
  }

  handleChange() {
    if (this.input) {
      const inputValue = this.input.value;
      if (inputValue) {
        const date = new Date(inputValue);
        if (!isNaN(date)) {
          this.value = date;
        }
      } else {
        this.value = null;
      }
    }
    this.emitEvent('change', { detail: this.value });
  }

  async handleFocus() {
    this.focused = true;
    super.handleFocus();

    if (this.readOnly) {
      return;
    }

    this._oldValue = this._date;

    if (!this._value) {
      await this.updateComplete;
      this.select();
    }
  }

  handleBlur() {
    super.handleBlur();
  }

  renderInput() {
    const inputValue =
      this.value instanceof Date && !isNaN(this.value)
        ? this.value.toISOString().split('T')[0]
        : '';

    return html`
      <input
        type=${this.type}
        ${ref((el) => (this.input = el))}
        .value=${live(inputValue)}
        id=${ifDefined(this.id)}
        autocomplete=${ifDefined(this.autocomplete)}
        placeholder=${ifDefined(this.placeholder)}
        aria-label=${ifDefined(this.label)}
        aria-placeholder=${ifDefined(this.placeholder)}
        aria-describedby=${`${this.id}_${this.type}_error_message`}
        ?readonly=${this.readOnly}
        ?disabled=${this.disabled}
        @blur=${this.handleBlur}
        @focus=${this.handleFocus}
        @input=${super.handleInput}
        @keydown=${super.handleKeydown}
        @click=${this.handleClick}
        @cut=${this.handleCut}
        @change=${this.handleChange}
        @compositionstart=${this.handleCompositionStart}
        @compositionend=${this.handleCompositionEnd}
        @dragenter=${this.handleDragEnter}
        @dragleave=${this.handleDragLeave}
        @dragstart=${this.handleDragStart}
      />
      <input
        type="hidden"
        class="${this.formattedDate ? 'has-value' : ''}"
        id=${`${this.id}_hidden`}
        name=${ifDefined(this.name)}
        value=${this.formattedDate}
      />
    `;
  }
}

window.customElements.get('float-date-field') ||
  window.customElements.define('float-date-field', FloatDateField);
